import { useEffect, useMemo, useRef, useState } from "react";
import "./TrendsPage.css";

/**
 * TrendsSearchSelect
 *
 * Compact filterable single-select for the Trends tabs' Role dropdowns -
 * the React stand-in for the old filterable/clearable p-select over the full
 * job-title list (filter fields name + jobCode, item template
 * "{name} ({subStack})"). Territory dropdowns don't need this (6 options,
 * plain <select>); roles do (hundreds of options).
 *
 * Props:
 *   options     [{ value, label, sub }]   sub = secondary text in the row
 *   value       currently selected value (or null)
 *   placeholder shown when nothing selected
 *   onChange    (value|null) => void      null on clear
 */
export default function TrendsSearchSelect({ options = [], value = null, placeholder = "Select", onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = options.find((o) => o.value === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Same filter fields as the old p-select: name + jobCode.
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        String(o.value || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  return (
    <div className={`tr-sselect${open ? " open" : ""}`} ref={ref}>
      <button
        type="button"
        className="tr-sselect-trigger"
        onClick={() => {
          setOpen(!open);
          setQuery("");
        }}
      >
        <span className={selected ? "" : "tr-sselect-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="tr-sselect-icons">
          {selected && (
            <span
              className="tr-sselect-clear"
              role="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </span>
          )}
          <svg className="tr-sselect-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
        </span>
      </button>

      {open && (
        <div className="tr-sselect-panel">
          <input
            className="tr-sselect-search"
            placeholder="Search…"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="tr-sselect-list">
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`tr-sselect-item${o.value === value ? " selected" : ""}`}
                onClick={() => {
                  onChange?.(o.value);
                  setOpen(false);
                }}
              >
                <span>{o.label}</span>
                {o.sub && <span className="tr-sselect-sub">({o.sub})</span>}
              </button>
            ))}
            {filtered.length === 0 && <div className="tr-sselect-empty">No matches.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
